import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { config } from '../global/config';
import { logTrace } from '../telemetry/logTrace';

type CustomerStatusEvent = CustomEvent<{ statusCode: number }>;

export function useCustomerStatusErrorRedirect() {
  const auth = useAuth();
  const isRedirectingRef = useRef(false);

  useEffect(() => {
    const handleCustomerError = async (event: Event) => {
      const { statusCode } = (event as CustomerStatusEvent).detail;

      logTrace('useCustomerStatusErrorRedirect: event received', {
        statusCode,
        alreadyRedirecting: isRedirectingRef.current,
        isAuthenticated: auth.isAuthenticated,
        hasUser: !!auth.user,
      });

      if (isRedirectingRef.current) {
        return;
      }

      isRedirectingRef.current = true;

      logTrace(
        'useCustomerStatusErrorRedirect: calling signoutRedirect',
        { hasUser: !!auth.user },
        { flush: true },
      );
      // useAuth().signoutRedirect never throws: once the redirect starts it
      // never resolves, and on failure it returns null (the error is only
      // put in auth.error). Getting past this line means the signout failed.
      await auth.signoutRedirect({
        post_logout_redirect_uri: config.idpPostLogoutRedirectUri ?? '/',
        extraQueryParams: {
          skip_logout_prompt: 'true',
          error_code: String(statusCode),
        },
      });

      logTrace('useCustomerStatusErrorRedirect: signoutRedirect failed');
      try {
        await auth.removeUser();
      } catch (err) {
        console.error('SignoutRedirect error:', err);
      }

      logTrace(
        'useCustomerStatusErrorRedirect: redirecting to IDP error page',
        { statusCode },
      );
      globalThis.location.replace(
        `${config.idpAuthority}/Account/Login/UserStatus/Error?errorCode=${statusCode}`,
      );
    };

    globalThis.addEventListener(
      'auth:customer-status-error',
      handleCustomerError as any,
    );

    return () => {
      globalThis.removeEventListener(
        'auth:customer-status-error',
        handleCustomerError as any,
      );
    };
  }, [auth]);
}
